import React from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { useStyles } from './CloudPropensityTierBar.styles'

/** Visual tier: high (3 bars), medium (2 bars), low (1 bar), or unknown */
export type CloudPropensityTier = 'high' | 'medium' | 'low' | '-'

/** Bar heights for the three segments (px) */
const TIER_BAR_HEIGHTS = [7, 11, 16] as const

/** Config for how a single tier (high/medium/low) is rendered */
export interface TierBarConfig {
  /** Number of bars filled (1–3). Default: high=3, medium=2, low=1 */
  filledBars?: 1 | 2 | 3
  /** Color for filled bars. Default: theme success / warning / grey */
  activeColor?: string
  /** Color for unfilled bars. Default: theme.palette.grey[300] */
  inactiveColor?: string
}

export type TierContextConfig = Partial<
  Record<Exclude<CloudPropensityTier, '-'>, TierBarConfig>
>

/** Resolve High / Medium / Low / inactive colors from the Labra theme palette. */
export const getTierSignalColors = (theme: Theme) => ({
  high: theme.palette.success.main,
  medium: theme.palette.warning.main,
  low: theme.palette.grey[400],
  inactive: theme.palette.grey[300],
})

/** Named preset contexts. Add more for different screens (e.g. score, engagement). */
export const TIER_CONTEXT_PRESETS: Record<string, TierContextConfig> = {
  default: {
    high: { filledBars: 3 },
    medium: { filledBars: 2 },
    low: { filledBars: 1 },
  },
  propensity: {
    high: { filledBars: 3 },
    medium: { filledBars: 2 },
    low: { filledBars: 1 },
  },
}

const DEFAULT_FILLED_BY_TIER: Record<
  Exclude<CloudPropensityTier, '-'>,
  1 | 2 | 3
> = {
  high: 3,
  medium: 2,
  low: 1,
}

export interface CloudPropensityTierBarProps {
  tier: CloudPropensityTier
  /**
   * Named context (e.g. 'default', 'propensity'). Uses TIER_CONTEXT_PRESETS.
   * Ignored if tierConfig is provided.
   */
  context?: keyof typeof TIER_CONTEXT_PRESETS | string
  /**
   * Explicit per-tier config. Overrides context when provided.
   * Use for one-off or custom contexts.
   */
  tierConfig?: TierContextConfig
}

function getFilledBars(
  tier: Exclude<CloudPropensityTier, '-'>,
  props: CloudPropensityTierBarProps
): 1 | 2 | 3 {
  const baseFilled = DEFAULT_FILLED_BY_TIER[tier]
  const preset = props.tierConfig
    ? undefined
    : props.context != null
      ? TIER_CONTEXT_PRESETS[props.context]
      : TIER_CONTEXT_PRESETS.default
  return (
    props.tierConfig?.[tier]?.filledBars ??
    preset?.[tier]?.filledBars ??
    baseFilled
  )
}

function getCustomColors(
  tier: Exclude<CloudPropensityTier, '-'>,
  props: CloudPropensityTierBarProps
): { activeColor?: string; inactiveColor?: string } {
  const preset = props.tierConfig
    ? undefined
    : props.context != null
      ? TIER_CONTEXT_PRESETS[props.context]
      : TIER_CONTEXT_PRESETS.default
  return {
    activeColor:
      props.tierConfig?.[tier]?.activeColor ?? preset?.[tier]?.activeColor,
    inactiveColor:
      props.tierConfig?.[tier]?.inactiveColor ?? preset?.[tier]?.inactiveColor,
  }
}

const ACTIVE_CLASS_BY_TIER = {
  high: 'tierBarActiveHigh',
  medium: 'tierBarActiveMedium',
  low: 'tierBarActiveLow',
} as const

export const CloudPropensityTierBar: React.FC<
  CloudPropensityTierBarProps
> = props => {
  const { tier } = props
  const theme = useTheme()
  const { classes, cx } = useStyles()
  const resolvedTier = tier === '-' ? 'high' : tier
  const filledBars = getFilledBars(resolvedTier, props)
  const customColors = getCustomColors(resolvedTier, props)
  const defaults = getTierSignalColors(theme)
  const activeColor = customColors.activeColor ?? defaults[resolvedTier]
  const inactiveColor = customColors.inactiveColor ?? defaults.inactive
  const useInlineColors = Boolean(
    customColors.activeColor || customColors.inactiveColor
  )

  if (tier === '-') {
    return (
      <div
        className={classes.tierBarContainer}
        data-testid="cloud-propensity-tier-unknown"
      >
        {TIER_BAR_HEIGHTS.map((height, index) => (
          <div
            key={index}
            className={cx(classes.tierBar, classes.tierBarInactive)}
            data-testid="cloud-propensity-tier-bar"
            data-filled="false"
            style={{ height }}
          />
        ))}
      </div>
    )
  }

  const activeClass = classes[ACTIVE_CLASS_BY_TIER[tier]]

  return (
    <div
      className={classes.tierBarContainer}
      data-testid="cloud-propensity-tier-container"
    >
      {TIER_BAR_HEIGHTS.map((height, index) => {
        const filled = index < filledBars
        return (
          <div
            key={index}
            className={cx(
              classes.tierBar,
              !useInlineColors &&
                (filled ? activeClass : classes.tierBarInactive)
            )}
            data-testid="cloud-propensity-tier-bar"
            data-filled={filled ? 'true' : 'false'}
            data-tier={tier}
            {...(useInlineColors
              ? {
                  'data-bg-color': filled ? activeColor : inactiveColor,
                }
              : null)}
            style={{
              height,
              ...(useInlineColors
                ? { backgroundColor: filled ? activeColor : inactiveColor }
                : null),
            }}
          />
        )
      })}
    </div>
  )
}
